import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, CheckCircle2 } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";

/**
 * FeatureSpotlight — a tall, two-column product feature section.
 *
 * Designed to make ONE feature feel like the entire page for 60 seconds:
 *   - Copy column: tag → headline → lead → 3-4 spec bullets → primary stat → CTA
 *   - Visual column: a bespoke render-prop block (a product mock, not a screenshot)
 *   - Alternates left/right via `side`
 *
 * Used on Home to spotlight Call IQ, Ask KAI, Clinic Pulse, Reactivation.
 */

const ACCENTS = {
  rose: {
    chip: "border-rose-500/30 bg-rose-500/[0.08] text-rose-700 dark:text-rose-300",
    glow: "from-rose-500/15 via-amber-500/8 to-transparent",
    bg:   "bg-gradient-to-br from-rose-500 via-rose-600 to-orange-600",
    stat: "from-rose-500 to-orange-500",
  },
  violet: {
    chip: "border-violet-500/30 bg-violet-500/[0.08] text-violet-700 dark:text-violet-300",
    glow: "from-violet-500/15 via-fuchsia-500/8 to-transparent",
    bg:   "bg-gradient-to-br from-violet-500 via-fuchsia-500 to-purple-600",
    stat: "from-violet-500 to-fuchsia-500",
  },
  emerald: {
    chip: "border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-700 dark:text-emerald-300",
    glow: "from-emerald-500/15 via-teal-500/8 to-transparent",
    bg:   "bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-500",
    stat: "from-emerald-500 to-teal-500",
  },
  blue: {
    chip: "border-blue-500/30 bg-blue-500/[0.08] text-blue-700 dark:text-blue-300",
    glow: "from-blue-500/15 via-sky-500/8 to-transparent",
    bg:   "bg-gradient-to-br from-blue-500 via-sky-500 to-violet-500",
    stat: "from-blue-500 to-sky-500",
  },
  amber: {
    chip: "border-amber-500/30 bg-amber-500/[0.08] text-amber-700 dark:text-amber-300",
    glow: "from-amber-500/15 via-orange-500/8 to-transparent",
    bg:   "bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500",
    stat: "from-amber-500 to-rose-500",
  },
};

export const FeatureSpotlight = ({
  id,
  testid,
  tag,
  title,
  highlight,
  lead,
  bullets = [],
  stat,
  cta,
  visual,
  accent = "rose",
  side = "right", // visual side
  className = "",
}) => {
  const a = ACCENTS[accent] || ACCENTS.rose;
  const openDemo = useDemo();
  const visualFirst = side === "left";

  const onCta = (e) => {
    if (cta?.action === "demo") {
      e?.preventDefault();
      openDemo();
    }
  };

  return (
    <section
      id={id}
      data-testid={testid || `spotlight-${id}`}
      className={`relative overflow-hidden py-12 sm:py-16 ${className}`}
    >
      <div className={`pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br ${a.glow} opacity-[0.5]`} />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className={`grid items-center gap-12 lg:gap-16 ${visual ? "lg:grid-cols-[0.95fr_1fr]" : "lg:grid-cols-1"} ${visualFirst ? "lg:grid-flow-dense" : ""}`}>
          {/* COPY column */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className={visualFirst ? "lg:col-start-2" : ""}
          >
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.2em] ${a.chip}`}>
              {tag}
            </span>

            <h2
              data-testid={`${testid || `spotlight-${id}`}-title`}
              className="mt-4 font-display text-[1.75rem] font-black leading-[1.02] tracking-[-0.025em] sm:text-[2.125rem] lg:text-[2.5rem]"
            >
              {title}{" "}
              {highlight && (
                <span className={`bg-gradient-to-br ${a.stat} bg-clip-text text-transparent`}>
                  {highlight}
                </span>
              )}
            </h2>

            <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15.5px]">
              {lead}
            </p>

            {bullets.length > 0 && (
              <ul className="mt-5 space-y-2.5">
                {bullets.map((b, i) => (
                  <li
                    key={i}
                    data-testid={`${testid || `spotlight-${id}`}-bullet-${i}`}
                    className="flex items-start gap-2.5 text-[13px] leading-snug text-foreground/85 sm:text-[14px]"
                  >
                    <span className={`mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-md ${a.bg} text-white`}>
                      <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            {stat && (
              <div className="mt-6">
                <span className={`block font-display text-[3rem] font-black leading-[0.9] tracking-[-0.045em] sm:text-[3.75rem] bg-gradient-to-br ${a.stat} bg-clip-text text-transparent`}>
                  {stat.value}
                </span>
                <span className="mt-3 block max-w-md text-[12.5px] leading-snug text-muted-foreground sm:text-[13.5px]">
                  {stat.label}
                </span>
              </div>
            )}

            {cta && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {cta.action === "demo" ? (
                  <button
                    type="button"
                    data-testid={`${testid || `spotlight-${id}`}-cta`}
                    onClick={onCta}
                    className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <CalendarCheck className="h-4 w-4" />
                    {cta.label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ) : (
                  <Link
                    to={cta.to}
                    data-testid={`${testid || `spotlight-${id}`}-cta`}
                    className="group inline-flex h-11 items-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    {cta.label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                )}
                {cta.secondary && (
                  <Link
                    to={cta.secondary.to}
                    data-testid={`${testid || `spotlight-${id}`}-cta-secondary`}
                    className="inline-flex h-11 items-center gap-2 rounded-xl border border-foreground/15 bg-card px-4 text-[13px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                  >
                    {cta.secondary.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            )}
          </motion.div>

          {/* VISUAL column */}
          {visual && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className={visualFirst ? "lg:col-start-1 lg:row-start-1" : ""}
            >
              {visual}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureSpotlight;
